import { useEffect, useRef } from 'react';
import useReducedMotion from '../hooks/useReducedMotion';

const BLOBS = [
  { freq: 1.0, phaseX: 0.0, phaseY: 0.5, baseCX: 0.25, baseCY: 0.25, radius: 0.55, r: 34,  g: 0,   b: 170 },
  { freq: 0.7, phaseX: 1.2, phaseY: 2.0, baseCX: 0.75, baseCY: 0.35, radius: 0.50, r: 0,   g: 68,  b: 136 },
  { freq: 1.3, phaseX: 2.4, phaseY: 0.8, baseCX: 0.50, baseCY: 0.70, radius: 0.48, r: 85,  g: 0,   b: 51  },
  { freq: 0.9, phaseX: 3.1, phaseY: 1.5, baseCX: 0.20, baseCY: 0.80, radius: 0.44, r: 0,   g: 51,  b: 34  },
  { freq: 1.1, phaseX: 1.7, phaseY: 3.3, baseCX: 0.80, baseCY: 0.75, radius: 0.46, r: 68,  g: 0,   b: 136 },
];

function drawFrame(ctx, w, h, t) {
  ctx.clearRect(0, 0, w, h);
  ctx.fillStyle = '#04060F';
  ctx.fillRect(0, 0, w, h);
  ctx.globalCompositeOperation = 'screen';

  BLOBS.forEach(blob => {
    const cx = (blob.baseCX + 0.18 * Math.sin(t * 2 * Math.PI * blob.freq + blob.phaseX)) * w;
    const cy = (blob.baseCY + 0.16 * Math.cos(t * 2 * Math.PI * blob.freq + blob.phaseY)) * h;
    const r  = blob.radius * Math.max(w, h);
    const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    grad.addColorStop(0, `rgba(${blob.r},${blob.g},${blob.b},0.30)`);
    grad.addColorStop(1, `rgba(${blob.r},${blob.g},${blob.b},0)`);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);
  });

  ctx.globalCompositeOperation = 'source-over';
}

export default function PlasmaBackground() {
  const canvasRef    = useRef(null);
  const rafRef       = useRef(null);
  const startRef     = useRef(null);
  const reducedMotion = useReducedMotion();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');

    function sync() {
      canvas.width  = canvas.offsetWidth  || 1;
      canvas.height = canvas.offsetHeight || 1;
    }
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(canvas);

    if (reducedMotion) {
      drawFrame(ctx, canvas.width, canvas.height, 0);
      return () => ro.disconnect();
    }

    function tick(ts) {
      if (!startRef.current) startRef.current = ts;
      const t = ((ts - startRef.current) / 30000) % 1;
      drawFrame(ctx, canvas.width, canvas.height, t);
      rafRef.current = requestAnimationFrame(tick);
    }
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      ro.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [reducedMotion]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 0,
      }}
    />
  );
}
